// ---------------------------------------------------------------------------
// 직전에 성공한(또는 시도한) 로그인 방법. 로그인 화면이 이 값을 읽어 소셜 버튼을
// "최근 사용"으로 맨 위에 끌어올린다 — 재방문자가 "저번에 뭘로 들어왔지?"를 다시
// 찾지 않게 하는 게 목적이다.
//
// 왜 서버가 아니라 localStorage 인가: 이 값은 세션이 서기 "전" 화면에서 필요하다.
// better-auth 의 account(프로바이더 기록)는 로그인에 성공해야 읽히므로 로그인 화면에서는
// 쓸 수 없다. 그래서 언어 선호값(shared/i18n/locale-preference.ts)과 똑같이
// "localStorage + useSyncExternalStore + 탭 간 storage 동기화" 패턴을 그대로 미러링한다.
// ---------------------------------------------------------------------------

import { useSyncExternalStore } from "react";
import type { SocialProviderId } from "@app/ui";

/** 저장 가능한 로그인 방법. "email" = 이메일/비밀번호, 그 외는 소셜 프로바이더 id. */
export type AuthMethod = "email" | SocialProviderId;

const STORAGE_KEY = "withtask.last-auth-method";

function readStorage(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    // localStorage 접근 불가(사생활 보호 모드 등)면 "최근 사용" 없이 동작
    return null;
  }
}

const listeners = new Set<() => void>();

function notify() {
  for (const listener of listeners) listener();
}

/** 로그인에 성공(소셜은 리다이렉트 직전)했을 때 방금 쓴 방법을 기록한다. */
export function setLastAuthMethod(method: AuthMethod) {
  try {
    localStorage.setItem(STORAGE_KEY, method);
  } catch {
    // 저장 실패해도 인증 흐름 자체엔 영향 없음(다음 방문에 "최근 사용" 강조만 빠진다)
  }
  notify();
}

/** 직전 로그인 방법을 읽는다. 없으면 null. 반환은 raw 문자열 — 로그인 화면의 정렬이
 * 알려진 프로바이더 id 와 대조하므로, 과거 포맷/미지의 값은 자연히 무시된다. */
export function getLastAuthMethod(): string | null {
  return readStorage();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** 로그인 화면이 "최근 사용" 강조에 쓰는 구독 훅. */
export function useLastAuthMethod(): string | null {
  return useSyncExternalStore(subscribe, getLastAuthMethod);
}

// 같은 브라우저의 다른 탭에서 로그인해 값이 바뀐 경우 따라간다.
// (서버/테스트 등 브라우저가 아닌 환경에서 import 될 수 있으므로 가드한다.)
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    // 이 키가 아닌 변경은 무시
    const isUnrelatedKey = event.key !== STORAGE_KEY;
    if (isUnrelatedKey) return;
    notify();
  });
}
